// Version check service to detect when application has been updated
class VersionCheck {
    constructor() {
        this.currentVersion = process.env.VUE_APP_VERSION || this.getBuildTimestamp();
        this.versionKey = 'app_version';
        this.checkInterval = 600000; // 10 minutes
        this.intervalId = null;
        this.failedChecks = 0;
        this.maxFailedChecks = 3; // Require multiple failed checks before showing notification
        this.lastNotificationTime = 0;
        this.notificationCooldown = 1800000; // 30 minutes cooldown between notifications
    }

    getBuildTimestamp() {
        // Get the version from the meta tag which is set during build time
        const metaVersion = document.querySelector('meta[name="app-version"]');
        if (metaVersion) {
            return metaVersion.getAttribute('content');
        }
        // Fallback to a static timestamp for current session
        if (!this.staticVersion) {
            this.staticVersion = Date.now().toString();
        }
        return this.staticVersion;
    }

    init() {
        // Store current version on first load
        this.storeCurrentVersion();
        
        // Start periodic version checks
        this.startVersionCheck();
    }

    storeCurrentVersion() {
        localStorage.setItem(this.versionKey, this.currentVersion);
    }

    getStoredVersion() {
        return localStorage.getItem(this.versionKey);
    }

    checkForUpdates() {
        return new Promise((resolve) => {
            // First, check if we're within cooldown period
            const now = Date.now();
            if (now - this.lastNotificationTime < this.notificationCooldown) {
                resolve(false);
                return;
            }

            // Get the latest version from server
            this.fetchLatestVersion().then((latestVersion) => {
                const storedVersion = this.getStoredVersion();
                
                if (latestVersion && storedVersion && storedVersion !== latestVersion) {
                    // Version mismatch detected - verify with asset check
                    this.checkAssetExists().then((assetsValid) => {
                        if (!assetsValid) {
                            this.failedChecks++;
                            console.log(`Version check failed attempt ${this.failedChecks}/${this.maxFailedChecks}`);
                            
                            // Only trigger notification after multiple consecutive failures
                            if (this.failedChecks >= this.maxFailedChecks) {
                                this.failedChecks = 0; // Reset counter
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                        } else {
                            // Assets are valid, reset failed checks counter
                            this.failedChecks = 0;
                            resolve(false);
                        }
                    }).catch(() => {
                        // Network error - don't trigger notification on single failure
                        console.log('Version check network error, ignoring');
                        resolve(false);
                    });
                } else {
                    // Versions match or no stored version, reset failed checks
                    this.failedChecks = 0;
                    resolve(false);
                }
            }).catch(() => {
                // Failed to fetch latest version - don't trigger notification
                console.log('Failed to fetch latest version, ignoring');
                resolve(false);
            });
        });
    }

    fetchLatestVersion() {
        return new Promise((resolve, reject) => {
            // Fetch the current page to get the latest version from meta tag
            fetch(window.location.origin, {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            }).then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.text();
            }).then(html => {
                // Parse HTML to extract version from meta tag
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const metaVersion = doc.querySelector('meta[name="app-version"]');
                
                if (metaVersion) {
                    const version = metaVersion.getAttribute('content');
                    resolve(version);
                } else {
                    reject('No version meta tag found');
                }
            }).catch(error => {
                console.error('Error fetching latest version:', error);
                reject(error);
            });
        });
    }

    checkAssetExists() {
        return new Promise((resolve) => {
            // More robust asset checking - look for specific patterns that indicate a new build
            const timeout = setTimeout(() => {
                console.log('Asset check timeout, assuming valid');
                resolve(true); // Timeout - assume assets are valid
            }, 5000);

            fetch(window.location.href, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            }).then(response => {
                clearTimeout(timeout);
                if (!response.ok) {
                    throw new Error('Response not ok');
                }
                return response.text();
            }).then(html => {
                // Parse HTML to check if script sources have changed
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newScripts = Array.from(doc.querySelectorAll('script[src*="js/"]'));
                const currentScripts = Array.from(document.querySelectorAll('script[src*="js/"]'));
                
                // If script count changed significantly, likely a new build
                if (Math.abs(newScripts.length - currentScripts.length) > 1) {
                    console.log('Script count changed significantly, update detected');
                    resolve(false);
                    return;
                }
                
                // Check for hash differences in main app scripts
                const newMainScripts = newScripts.filter(script => 
                    script.src && script.src.includes('app.') && script.src.includes('.js')
                );
                const currentMainScripts = currentScripts.filter(script => 
                    script.src && script.src.includes('app.') && script.src.includes('.js')
                );
                
                if (newMainScripts.length > 0 && currentMainScripts.length > 0) {
                    const newMainSrc = newMainScripts[0].src;
                    const currentMainSrc = currentMainScripts[0].src;
                    
                    if (newMainSrc !== currentMainSrc) {
                        console.log('Main app script hash changed, update detected');
                        resolve(false);
                        return;
                    }
                }
                
                // Assets appear to be the same
                resolve(true);
            }).catch(error => {
                clearTimeout(timeout);
                console.log('Asset check failed:', error);
                resolve(true); // On error, assume assets are valid to avoid false positives
            });
        });
    }

    startVersionCheck() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }

        this.intervalId = setInterval(() => {
            this.checkForUpdates().then((updateAvailable) => {
                if (updateAvailable) {
                    this.notifyUserOfUpdate();
                }
            });
        }, this.checkInterval);
    }

    stopVersionCheck() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    notifyUserOfUpdate() {
        // Record notification time for cooldown
        this.lastNotificationTime = Date.now();
        
        // Stop checking once we've found an update
        this.stopVersionCheck();
        
        // Create a non-intrusive notification
        this.showUpdateNotification();
    }

    showUpdateNotification() {
        // Check if notification already exists
        if (document.getElementById('version-update-notification')) {
            return;
        }

        const notification = document.createElement('div');
        notification.id = 'version-update-notification';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #2196F3;
            color: white;
            padding: 12px 20px;
            border-radius: 4px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
            z-index: 10000;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            max-width: 300px;
            cursor: pointer;
            transition: all 0.3s ease;
        `;
        
        notification.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: space-between;">
                <div>
                    <strong>Update Available</strong><br>
                    <small>Click to refresh and get the latest version</small>
                </div>
                <div style="margin-left: 15px; font-size: 18px; cursor: pointer;" id="dismiss-notification">&times;</div>
            </div>
        `;

        // Handle click on main notification area (refresh)
        notification.addEventListener('click', (e) => {
            if (e.target.id !== 'dismiss-notification') {
                this.refreshPage();
            }
        });

        // Handle click on dismiss button
        const dismissBtn = notification.querySelector('#dismiss-notification');
        dismissBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.dismissNotification();
        });

        // Add hover effect
        notification.onmouseenter = () => {
            notification.style.transform = 'translateY(-2px)';
            notification.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
        };

        notification.onmouseleave = () => {
            notification.style.transform = 'translateY(0)';
            notification.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
        };

        document.body.appendChild(notification);

        // Auto-hide after 30 seconds if not clicked
        setTimeout(() => {
            if (document.getElementById('version-update-notification')) {
                notification.style.opacity = '0.7';
            }
        }, 30000);
    }

    dismissNotification() {
        const notification = document.getElementById('version-update-notification');
        if (notification) {
            notification.style.opacity = '0';
            notification.style.transform = 'translateX(100%)';
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 300);
        }
        
        // Set cooldown to prevent immediate re-notification
        this.lastNotificationTime = Date.now();
        
        // Restart version checking after a longer delay
        setTimeout(() => {
            this.startVersionCheck();
        }, 300000); // 5 minutes before checking again
    }

    refreshPage() {
        // Clear stored version to prevent immediate re-notification
        localStorage.removeItem(this.versionKey);
        
        // Force a hard refresh
        if ('serviceWorker' in navigator) {
            // If there's a service worker, unregister it first
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                for (let registration of registrations) {
                    registration.unregister();
                }
                window.location.reload(true);
            });
        } else {
            window.location.reload(true);
        }
    }

    // Manual check method for testing
    manualCheck() {
        return this.checkForUpdates();
    }

    // Reset failed checks and cooldown - useful for testing or manual resets
    resetChecker() {
        this.failedChecks = 0;
        this.lastNotificationTime = 0;
        console.log('Version checker reset');
    }

    // Get current status for debugging
    getStatus() {
        return {
            currentVersion: this.currentVersion,
            storedVersion: this.getStoredVersion(),
            failedChecks: this.failedChecks,
            maxFailedChecks: this.maxFailedChecks,
            lastNotificationTime: this.lastNotificationTime,
            cooldownRemaining: Math.max(0, this.notificationCooldown - (Date.now() - this.lastNotificationTime))
        };
    }
}

export default new VersionCheck(); 